import { Box, CircularProgress } from "@mui/material";
import { type JSX, useMemo } from "react";
import { useParams } from "react-router";
import { logger } from "@platform/browser-logger";
import { CoverageTypeEnum } from "@platform/types";
import {
  useRfpCoverages,
  mapCoveragePlans,
  type Coverage,
  type RfpCoveragesResponse,
} from "@platform/api";
import { Typography } from "@platform/design-system";

import { CoverageDetailsPanel } from "./CoverageDetailsPanel";
import { withCommissionText } from "./withCommissionText";

/**
 * Maps CoverageTypeEnum to the coverage names returned by the RFP coverages API.
 * These differ from coverageTypeToLabel which returns UI-friendly labels.
 */
const COVERAGE_TYPE_TO_API_NAME: Record<CoverageTypeEnum, string> = {
  [CoverageTypeEnum.DENTAL]: "Dental",
  [CoverageTypeEnum.DENTAL_PPO]: "Dental PPO",
  [CoverageTypeEnum.DENTAL_HMO]: "Dental HMO",
  [CoverageTypeEnum.VISION]: "Vision",
  [CoverageTypeEnum.STD]: "Short Term Disability",
  [CoverageTypeEnum.LTD]: "Long Term Disability",
  [CoverageTypeEnum.BASIC_LIFE_ADND]: "Basic Life/AD&D",
  [CoverageTypeEnum.VOLUNTARY_LIFE_ADND]: "Voluntary Life/AD&D",
  [CoverageTypeEnum.ACCIDENT]: "Accident Insurance",
  [CoverageTypeEnum.CRITICAL_ILLNESS]: "Critical Illness Insurance",
  [CoverageTypeEnum.HOSPITAL_INDEMNITY]: "Hospital Indemnity Insurance",
  [CoverageTypeEnum.CANCER]: "Cancer Insurance",
  [CoverageTypeEnum.FMLA]: "Family Medical Leave Administration",
  [CoverageTypeEnum.EAP]: "Employee Assistance Program",
  [CoverageTypeEnum.INDIVIDUAL_DISABILITY_INSURANCE]:
    "Individual Disability Insurance",
  [CoverageTypeEnum.WHOLE_LIFE]: "Whole Life",
};

/**
 * Finds the matching coverage from the API response by comparing coverage names
 */
const findCoverageByType = (
  response: RfpCoveragesResponse | undefined,
  coverageType: CoverageTypeEnum,
): Coverage | null => {
  if (!response) return null;

  const targetName = COVERAGE_TYPE_TO_API_NAME[coverageType];
  const allCoverages = [
    ...(response.currentCoverages || []),
    ...(response.newCoverages || []),
  ];

  return allCoverages.find((cov) => cov.name === targetName) || null;
};

/**
 * RfpCoverageDetailsPanel
 *
 * Fetches and displays coverage details for a specific RFP and coverage type.
 * Extracts rfpId and coverageType from URL parameters.
 * Calls useRfpCoverages hook internally and handles loading/error states.
 */
export const RfpCoverageDetailsPanel = (): JSX.Element => {
  const { id: rfpId, planName } = useParams<{
    id: string;
    planName: string;
  }>();

  // Decode the URL-encoded planName parameter
  const coverageType = planName
    ? (decodeURIComponent(planName) as CoverageTypeEnum)
    : undefined;

  const { data, isLoading, error } = useRfpCoverages({
    rfpId: rfpId || "",
    enabled: !!rfpId,
  });

  const plans = useMemo(() => {
    if (!coverageType) return [];

    const coverage = findCoverageByType(data, coverageType);
    if (!coverage) return [];

    return mapCoveragePlans(withCommissionText(coverage)) ?? [];
  }, [data, coverageType]);

  // Check if required params are missing
  if (!rfpId || !coverageType) {
    logger.error(
      "[RfpCoverageDetailsPanel] Missing required URL parameters",
      new Error("Missing rfpId or coverageType from URL"),
      {
        rfpId,
        planName,
        coverageType,
        url: window.location.href,
      },
    );
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="danger" variant="body-medium">
          Something went wrong!
        </Typography>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          p: 4,
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="danger" variant="body-medium">
          Failed to load coverage details
        </Typography>
      </Box>
    );
  }

  if (plans.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body-medium" color="textSecondary">
          No coverage details available
        </Typography>
      </Box>
    );
  }

  return <CoverageDetailsPanel plans={plans} />;
};

export default RfpCoverageDetailsPanel;
